import { ChangeDetectorRef, ChangeDetectionStrategy, Component, HostListener, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormArray } from '@angular/forms';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import {
  Alert,
  AlertService,
  AlertType,
  ModalComponent,
  ModalResult,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig
} from '@michelin/theme';
import { debounceTime, distinctUntilChanged, map, Observable } from 'rxjs';
import { ProductDataService } from '../../services/product-data.service';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { Store } from '@ngxs/store';
import { CanComponentDeactivate } from '../../../../shared/guards/unsaved-changes.guard';
import {
  PaginatedRejectReasonInfo,
  PaginatedRejectReasonResponse,
  Pagination,
  RejectReason,
  RejectReasonFiltersResponse
} from '../../models/response.model';
import { ProductsState } from '../../../../shared/state/ProductsState';
import { ActivatedRoute } from '@angular/router';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { hasRolePermission } from '../../../user-management/utils/UserSetupUtils';
import { ROLES } from '../../../authentication/authRoles.constants';
import { ExportResponse } from '../../../../shared/services/export-to-excel.service';
import { DealerState } from '../../../../shared/state/DealerState';
import { MatDialog } from '@angular/material/dialog';
import { ExportModalComponent, ExportModalData } from '../../../../shared/components/export-modal/export-modal.component';
import { ExportColumnDef } from '../../../../shared/models/export-column-def.model';

@Component({
  selector: 'app-reject-reasons',
  templateUrl: './reject-reasons.component.html',
  styleUrls: ['./reject-reasons.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RejectReasonsComponent implements OnInit, CanComponentDeactivate {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<Element>;
  @ViewChild('discardConfirmation', { static: true }) discardModal: ModalComponent;
  @ViewChild('discardContent') discardContent: TemplateRef<any>;

  public fb = inject(FormBuilder);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public cdr = inject(ChangeDetectorRef);
  public store = inject(Store);
  public route = inject(ActivatedRoute);
  public featureFlagsService = inject(FeatureflagsService);
  public alertService = inject(AlertService);
  private dialog = inject(MatDialog);

  filterForm: FormGroup;
  filterFormBackend: FormGroup;
  rejectReasonsList: RejectReason[] = [];
  rejectReasonsFilterList: string[] = [];
  rejectDescriptionFilterList: string[] = [];
  codeTypeFilterList: string[] = [];
  failureTypeFilterList: string[] = [];
  activeFilterList: string[] = ['Y', 'N'];
  finishedPartNumberList: string[] = [];
  displayedRowsForm: FormGroup = this.fb.group({ rows: this.fb.array([]) });
  saveEnabled = false;
  isModified = false;
  pagination: Pagination;
  currentPage = 1;
  pageSize = 10;
  pageSizeOptions = [5, 10, 15, 25, 50];
  productSaveDisabled = true;

  openExportModal(): void {
    const columns: ExportColumnDef[] = Object.entries(this.columnNames).map(([key, label]) => ({ key, label, selected: true }));
    const data: ExportModalData = {
      columns,
      dataFetcher: this.exportDataFetcher,
      fileNamePrefix: this.dealerName + '-RejectReasons-export',
      maxExportRows: 10000
    };
    this.dialog.open(ExportModalComponent, { data, width: '480px' });
  }

  tableConfig: TableConfig<RejectReason> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom',
      pageLabelOptions: {
        firstPageLabel: '',
        lastPageLabel: '',
        nextPageLabel: '',
        previousPageLabel: '',
        itemsPerPageLabel: this.translationService.translate('pageLabelOptionText')
      }
    },
    columns: {}
  };
  state = TableConfig.getState(this.tableConfig, this.route);

  fetcher: PaginatorFetcher<RejectReason> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    this.generateFilterForm();
    return this.productDataService.getRejectReasons({ ...this.filterFormBackend.value, ...filters }, filters.page, filters.limit).pipe(
      map((rejectReasonsResponse: PaginatedRejectReasonResponse) => {
        this.rejectReasonsList = rejectReasonsResponse.data.rejectReasons || [];
        this.pagination = rejectReasonsResponse.data.pagination;
        this.updateDisplayedRowsForm(this.rejectReasonsList);
        this.displayedRowsForm.markAsPristine();
        return {
          items: this.rejectReasonsList,
          count: this.pagination.totalRecords
        };
      })
    );
  };
  dealerName = this.store.selectSnapshot(DealerState.currentDealer).name;
  columnNames: Record<string, string> = {};

  exportDataFetcher: () => Observable<ExportResponse> = () =>
    this.productDataService.getRejectReasons(this.filterFormBackend.value, 1, 10000).pipe(
      map((response: any) => ({
        data: (response.data.rejectReasons as RejectReason[]).map((item) => ({
          rejectReason: item.rejectReason,
          description: item.description,
          active: item.active,
          codeType: item.codeType,
          failureType: item.failureType,
          returnDispPartNum: item.returnDispPartNum,
          scrapDispPartNum: item.scrapDispPartNum
        }))
      }))
    );

  constructor() {
    this.filterForm = this.fb.group({
      rejectReasons: [''],
      rejectDescriptions: [''],
      codeTypes: [''],
      failureTypes: [''],
      active: ['']
    });

    this.filterFormBackend = this.fb.group({
      rejectReasons: [''],
      rejectDescriptions: [''],
      codeTypes: [''],
      failureTypes: [''],
      active: ['']
    });
  }

  ngOnInit(): void {
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    this.saveEnabled = !this.productSaveDisabled && hasRolePermission(ROLES.edit_product);
    if (this.productSaveDisabled) {
      this.alertService.showAlert({
        type: AlertType.classical,
        title: this.translationService.translate('readOnlyProductDataHeader'),
        content: this.translationService.translate('readOnlyProductDataText'),
        timestamp: 60000
      } as Alert);
    }
    this.loadFilterData();
    this.setupFilterSubscriptions();
    this.displayedRowsForm.valueChanges.subscribe(() => {
      this.isModified = this.checkIfModified();
    });
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.columnNames = {
        rejectReason: this.translationService.translate('rejectReasonText'),
        description: this.translationService.translate('rejectReasonDescriptionText'),
        active: this.translationService.translate('activeText'),
        codeType: this.translationService.translate('rejectCodeTypeText'),
        failureType: this.translationService.translate('failureTypeText'),
        returnDispPartNum: this.translationService.translate('returnPartNoText'),
        scrapDispPartNum: this.translationService.translate('scrapPartNoText')
      };

      this.tableConfig.columns = {
        rejectReason: { cell: (elm: RejectReason) => elm.rejectReason, header: this.translationService.translate('rejectReasonText') },
        rejectReasonDescription: {
          cell: (elm: RejectReason) => elm.description,
          header: this.translationService.translate('rejectReasonDescriptionText')
        },
        active: { cell: (elm: RejectReason) => elm.active, header: this.translationService.translate('activeText') },
        rejectCodeType: { cell: (elm: RejectReason) => elm.codeType, header: this.translationService.translate('rejectCodeTypeText') },
        failureType: { cell: (elm: RejectReason) => elm.failureType, header: this.translationService.translate('failureTypeText') },
        returnDispPartNum: { cell: 'returnDispPartNum', header: this.translationService.translate('returnPartNoText') },
        scrapDispPartNum: { cell: 'scrapDispPartNum', header: this.translationService.translate('scrapPartNoText') }
      };
      this.cdr.markForCheck();
    });
    this.updateDropdownLists();
  }

  public updateDisplayedRowsForm(rows: RejectReason[]) {
    const rowForms = rows.map((row) => this.createRowForm(row));
    const formArray = this.fb.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);
  }

  createRowForm(row: RejectReason): FormGroup {
    return this.fb.group({
      rejectReasonPk: [row.rejectReasonPk],
      rejectReason: [row.rejectReason],
      description: [row.description],
      codeType: [row.codeType],
      failureType: [row.failureType],
      active: [row.active],
      returnDispPartNum: [row.returnDispPartNum ?? null],
      scrapDispPartNum: [row.scrapDispPartNum ?? null]
    });
  }

  loadFilterData(): void {
    this.productDataService.getRejectReasonFilters().subscribe((response: RejectReasonFiltersResponse) => {
      this.rejectReasonsFilterList = response.data.rejectReasons || [];
      this.rejectDescriptionFilterList = response.data.rejectDescriptions || [];
      this.codeTypeFilterList = response.data.codeType || [];
      this.failureTypeFilterList = response.data.failureType || [];
    });
  }

  setupFilterSubscriptions(): void {
    this.filterForm.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
      this.generateFilterForm();
      this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
    });
  }

  generateFilterForm(): void {
    const formValue = this.filterForm.value;
    this.filterFormBackend.patchValue({
      rejectReasons: formValue.rejectReasons,
      rejectDescriptions: formValue.rejectDescriptions,
      codeTypes: formValue.codeTypes,
      failureTypes: formValue.failureTypes,
      active: formValue.active
    });
  }

  resetFilters(): void {
    this.filterForm.reset();
    this.filterFormBackend.reset();
    this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
  }

  updateDropdownLists(): void {
    this.finishedPartNumberList.push(...this.store.selectSnapshot(ProductsState.products).finishedPartNumbers);
  }

  save(): void {
    if (!this.saveEnabled) {
      return;
    }

    const modifiedRows = [];
    const formArray = this.displayedRowsForm.get('rows') as FormArray;

    for (let i = 0; i < this.rejectReasonsList.length; i++) {
      const original = { ...this.rejectReasonsList[i] };
      const current = { ...formArray.at(i).value };
      const isModified = Object.keys(current).some((key) => current[key] !== original[key]);
      if (isModified) {
        modifiedRows.push({ ...formArray.at(i).value, rejectReasonPk: this.rejectReasonsList[i].rejectReasonPk });
      }
    }

    if (modifiedRows.length > 0) {
      const payload: PaginatedRejectReasonInfo = {
        rejectReasons: modifiedRows,
        pagination: this.pagination
      };
      this.productDataService.updateRejectReasons(payload).subscribe({
        next: () => {
          this.alertService.showAlert({
            type: AlertType.success,
            content: this.translationService.translate('camSaveSuccessText')
          } as Alert);
          modifiedRows.forEach((modifiedRow) => {
            const index = this.rejectReasonsList.findIndex((row) => row.rejectReasonPk === modifiedRow.rejectReasonPk);
            if (index !== -1) {
              this.rejectReasonsList[index] = modifiedRow;
            }
          });
          this.updateDisplayedRowsForm(this.rejectReasonsList);
          this.displayedRowsForm.markAsPristine();
          this.isModified = false;
        },
        error: () => {
          this.alertService.showAlert({
            type: AlertType.critical,
            content: this.translationService.translate('criticalErrorAlertText')
          } as Alert);
        }
      });
    } else {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('noModifiedText')
      } as Alert);
    }
  }

  checkIfModified(): boolean {
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    const currentItems = this.rejectReasonsList;
    let modified = false;

    for (let i = 0; i < currentItems.length; i++) {
      const original = currentItems[i] as any;
      const current = formArray.at(i).value;
      if (Object.keys(current).some((key) => (current[key] ?? '') !== (original[key] ?? ''))) {
        modified = true;
        break;
      }
    }
    return modified;
  }

  getControl(item: any, controlName: keyof any): FormControl {
    const index = this.rejectReasonsList.findIndex((row) => row.rejectReasonPk === item.rejectReasonPk);
    if (index === -1) {
      return new FormControl('');
    }
    return (this.displayedRowsForm.get('rows') as FormArray).at(index).get(controlName as string) as FormControl;
  }

  showDiscardModal(): void {
    this.discardModal.open({
      id: 'discardConfirmation',
      title: this.translationService.translate('discardChangesText'),
      width: 550,
      content: this.discardContent,
      confirm: this.translationService.translate('okText'),
      cancel: this.translationService.translate('cancelText')
    });
  }

  onDiscardModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'discardConfirmation' && result.result) {
        this.updateDisplayedRowsForm(this.rejectReasonsList);
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
      }
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.checkIfModified()) {
      $event.returnValue = true;
    }
  }

  canDeactivate(): Promise<boolean> {
    if (this.checkIfModified()) {
      return new Promise<boolean>((resolve) => {
        this.showDiscardModal();
        const modalSubscription = this.discardModal.result.subscribe((result: ModalResult) => {
          if (result && result.id === 'discardConfirmation') {
            resolve(!!result.result);
            modalSubscription.unsubscribe();
          }
        });
      });
    }
    return Promise.resolve(true);
  }

  canEditRejectReasons() {
    return hasRolePermission(ROLES.edit_product);
  }
}
